import { useFlowSessionById, useUpdateFlowSession } from "@/api/flow_sessions";
import { useUpdateToolConfirmation } from "@/api/tool_confirmations";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCanvasReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import {
  ToolCall,
  ToolCallConfirmableRule,
  UpdateToolConfirmationPayload,
} from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import type { ErrorObject } from "ajv";
import {
  Boxes,
  Check,
  ChevronDown,
  CircleHelp,
  ShieldAlert,
  ShieldCheck,
  Terminal,
  X,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

interface ToolConfirmationStatusProps {
  toolCall: ToolCall;
  onComplete?: () => void;
}

export function ToolConfirmationStatus({
  toolCall,
}: ToolConfirmationStatusProps) {
  if (!toolCall?.user_confirmation_status) {
    return null;
  }

  if (toolCall.user_confirmation_status === "pending") {
    return (
      <div className="flex justify-end space-x-2">
        <div className="rounded-full w-2 h-2 bg-rust" />
      </div>
    );
  }

  if (toolCall.user_confirmation_status === "confirmed-processed") {
    return (
      <div className="flex justify-end space-x-2">
        <Check className="w-5 h-5 text-lc-active" />
      </div>
    );
  }

  if (toolCall.user_confirmation_status === "rejected") {
    return (
      <div className="flex text-xs text-muted-foreground justify-end space-x-2">
        Cancelled
      </div>
    );
  }

  if (toolCall.user_confirmation_status === "confirmed-processing") {
    return (
      <div className="flex justify-end space-x-2">
        <Spinner size="sm" />
      </div>
    );
  }

  return null;
}

interface ToolConfirmationComponentProps {
  flowSessionId: string;
  flowMessageId: string;
  toolCallArguments: object;
  toolCall: ToolCall;
  errors?: ErrorObject<string, Record<string, any>, unknown>[];
  onComplete?: () => void;
}

export function ToolConfirmationButtons({
  flowSessionId,
  flowMessageId,
  toolCallArguments,
  toolCall,
  errors,
  onComplete,
}: ToolConfirmationComponentProps) {
  const { mutate: updateConfirmation, mutateAsync: updateConfirmationAsync } =
    useUpdateToolConfirmation({
      onSuccess: () => {
        onComplete?.();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });

  // Read canvasId from the already-cached flow session (no extra network request).
  // Only canvas sessions carry a reasoning effort; non-canvas flows omit the field.
  const { data: flowSession } = useFlowSessionById(flowSessionId);
  const canvasId = flowSession?.canvas_id ?? undefined;
  const [agentReasoningEffort] = useCanvasReasoningEffort(canvasId);

  const { mutate: updateFlowSession } = useUpdateFlowSession({
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const buildConfirmPayload = (
    commandRules?: { pattern: string[] }[],
  ): UpdateToolConfirmationPayload => ({
    user_confirmation_status: "confirmed",
    tool_call_index: toolCall.index,
    // Arguments are pre-normalized by the JSONForms middleware in ToolCall.tsx
    tool_call_arguments: toolCallArguments,
    reasoning_effort: canvasId ? agentReasoningEffort : undefined,
    command_rules: commandRules,
  });

  const confirm = (commandRules?: { pattern: string[] }[]) => {
    if (toolCall.user_confirmation_status !== "pending") return;
    updateConfirmation({
      flowSessionId,
      flowMessageId,
      toolCallId: toolCall.id,
      payload: buildConfirmPayload(commandRules),
    });
  };

  const handleReject = () => {
    if (toolCall.user_confirmation_status !== "pending") return;
    updateConfirmation({
      flowSessionId,
      flowMessageId,
      toolCallId: toolCall.id,
      payload: {
        user_confirmation_status: "rejected",
        tool_call_index: toolCall.index,
        tool_call_arguments: toolCallArguments,
        reasoning_effort: canvasId ? agentReasoningEffort : undefined,
      },
    });
  };

  // Confirm the current command and persist the offered "always allow" rule(s)
  // so this exact command runs unprompted for the rest of the session.
  const handleAllowCommand = () =>
    confirm(offeredRules.map((r) => ({ pattern: r.pattern as string[] })));

  // Confirm the current command AND disable confirmations for the rest of the
  // session, so all subsequent commands run unprompted. The flag only affects
  // future tool calls, so the current pending one must still be confirmed here.
  //
  // The two writes are sequenced — the session bypass is only persisted once the
  // command confirmation succeeds — so a failure can't leave the session showing
  // bypass-enabled while the current command stays unconfirmed. We await
  // `mutateAsync` rather than chaining off a per-call `onSuccess` callback: the
  // confirmation's optimistic update flips this tool call out of "pending",
  // which immediately re-renders it into its resolved view and unmounts these
  // buttons. React Query (v5) does not fire `mutate()`'s per-call callbacks once
  // the caller has unmounted, so the old `onSuccess` chain silently dropped the
  // bypass write. The awaited promise resolves independently of mount state.
  const handleAllowAllSession = async () => {
    if (toolCall.user_confirmation_status !== "pending") return;
    try {
      await updateConfirmationAsync({
        flowSessionId,
        flowMessageId,
        toolCallId: toolCall.id,
        payload: buildConfirmPayload(),
      });
    } catch {
      // Confirmation failed; the hook's onError already surfaced a toast. Leave
      // the session bypass untouched so we never enable it without a confirm.
      return;
    }
    updateFlowSession({
      flowSessionId,
      data: { tool_user_confirmations_disabled: true },
    });
  };

  // Allow-listing affordances are e2b_bash-only. `offeredRules` are the
  // classifier's one-click rules (arbitrary-execution rules carry no pattern, so
  // they're filtered out). The blanket bypass is hidden once already enabled.
  const isE2bBash = toolCall.function.name === "Tool_E2b_Bash";
  const offeredRules: ToolCallConfirmableRule[] = isE2bBash
    ? (toolCall.confirmable_rules ?? []).filter(
        (r) => r.pattern && r.pattern.length > 0,
      )
    : [];
  const alreadyBypassed = !!flowSession?.tool_user_confirmations_disabled;
  const canAllowCommand = offeredRules.length > 0;
  const canAllowAllSession = isE2bBash && !alreadyBypassed;
  // The Accept button only grows a dropdown when there's an allow-list option to
  // offer; otherwise (non-CLI tools, or a session already bypassing) it stays a
  // plain button.
  const hasAllowOptions = canAllowCommand || canAllowAllSession;

  const [helpOpen, setHelpOpen] = useState(false);

  const disabled = !!errors && errors.length > 0;

  if (!toolCall?.user_confirmation_status) {
    return null;
  }

  if (toolCall.user_confirmation_status === "pending") {
    return (
      <>
        <CommandPermissionsHelpDialog
          open={helpOpen}
          onOpenChange={setHelpOpen}
        />
        <div className="flex justify-end items-center gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleReject();
            }}
          >
            <X />
            Cancel
          </Button>

          {hasAllowOptions ? (
            // Split Accept button: primary click runs the command once; the caret
            // opens "allow for session" (scoped) and "allow all" (blanket) options.
            <div className="inline-flex">
              <Button
                className="rounded-r-none"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  confirm();
                }}
              >
                <Check />
                Accept
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="rounded-l-none border-l border-primary-foreground/20 px-2"
                    disabled={disabled}
                    aria-label="More accept options"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ChevronDown />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-90">
                  {canAllowCommand && (
                    <DropdownMenuItem
                      className="flex-col items-start gap-0.5"
                      onSelect={handleAllowCommand}
                    >
                      <div className="flex items-center gap-2">
                        <ShieldCheck />
                        <span className="font-medium">
                          Auto-accept similar commands
                        </span>
                      </div>
                      <div className="pl-6 flex flex-col text-xs text-muted-foreground leading-snug">
                        {offeredRules.map((rule, i) => (
                          <span key={i}>
                            agent can{" "}
                            <span className="text-foreground">
                              {rule.summary}
                            </span>
                          </span>
                        ))}
                      </div>
                    </DropdownMenuItem>
                  )}
                  {canAllowCommand && canAllowAllSession && (
                    <DropdownMenuSeparator />
                  )}
                  {canAllowAllSession && (
                    <DropdownMenuItem
                      className="flex-col items-start gap-0.5 text-orange-600 focus:text-orange-600 dark:text-orange-500 dark:focus:text-orange-500"
                      onSelect={handleAllowAllSession}
                    >
                      <div className="flex items-center gap-2">
                        <ShieldAlert />
                        <span className="font-medium">
                          Auto-accept all commands this session
                        </span>
                      </div>
                      <span className="pl-6 text-xs text-muted-foreground leading-snug">
                        Skip confirmation for all agent commands
                      </span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-muted-foreground"
                    onSelect={() =>
                      // Defer so DropdownMenu can finish unmounting; avoids body
                      // stuck with pointer-events: none (Radix #3317).
                      window.setTimeout(() => setHelpOpen(true), 0)
                    }
                  >
                    <CircleHelp />
                    Help me decide
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                confirm();
              }}
            >
              <Check />
              Accept
            </Button>
          )}
        </div>
      </>
    );
  }

  return null;
}

export function CommandPermissionsHelpDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Terminal className="size-5 text-emerald-500" />
            Running commands
          </DialogTitle>
          <DialogDescription>
            To get things done, the agent works on a sandboxed computer —
            running shell commands, scripts, and CLI tools on your behalf.
            Commands that could change something pause here for your approval
            first.
          </DialogDescription>
        </DialogHeader>

        {/* Terminal banner — evokes the sandboxed computer the agent operates. */}
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 dark:bg-neutral-950 overflow-hidden">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-neutral-800">
            <span className="size-2.5 rounded-full bg-red-500/80" />
            <span className="size-2.5 rounded-full bg-amber-500/80" />
            <span className="size-2.5 rounded-full bg-emerald-500/80" />
            <span className="ml-2 flex items-center gap-1.5 font-mono text-xs text-neutral-400">
              <Terminal size={12} />
              agent@sandbox
            </span>
          </div>
          <pre className="px-3 py-2.5 font-mono text-sm text-neutral-100">
            <span className="text-emerald-400 select-none">agent@sandbox</span>
            <span className="text-neutral-500">:</span>
            <span className="text-sky-400">~</span>
            <span className="text-neutral-500">$ </span>
            <span className="animate-pulse">▍</span>
          </pre>
        </div>

        <ul className="flex flex-col gap-3 text-sm">
          <li className="flex gap-3">
            <Check className="size-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium">Accept</p>
              <p className="text-muted-foreground">
                Run this command once. The agent asks again next time.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <ShieldCheck className="size-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium">Auto-accept similar commands</p>
              <p className="text-muted-foreground">
                Stop asking for this and similar commands for the rest of this
                chat. Other commands still need your approval.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <ShieldAlert className="size-4 mt-0.5 shrink-0 text-orange-600 dark:text-orange-500" />
            <div>
              <p className="font-medium">
                Auto-accept all commands this session
              </p>
              <p className="text-muted-foreground">
                Skip confirmation for every command the agent runs for the rest
                of this chat. Convenient, but only use it when you trust what
                the agent is doing.
              </p>
            </div>
          </li>
        </ul>

        {/* The sandbox isn't isolated — the agent can also reach the user's
            connected integrations, so commands may touch real data in those
            apps. (A CLI-capable platform list isn't exposed to the client yet.) */}
        <div className="flex gap-2 rounded-md bg-muted/60 px-3 py-2.5 text-xs text-muted-foreground">
          <Boxes className="size-4 mt-0.5 shrink-0" />
          <span>
            Beyond the sandbox, the agent can act on your{" "}
            <span className="font-medium text-foreground">connected apps</span>{" "}
            on your behalf — so a command may read or change data in the
            services you've linked.
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
